import React, { useEffect, useState } from 'react';
import { Table, Button, Modal, Form, Select, Input } from 'antd';
import { EditOutlined, PlusOutlined } from '@ant-design/icons';

interface CommentsProps<
  T extends { id: number; comment: string; comment_for?: string; status: string; user_id: number; updated_at: string },
> {
  commentData: T[];
  status: string | null;
  itemId: string | null;
  storeFn: (id: number, data: any) => Promise<any>;
  updateFn: (id: number, commentId: number, data: any) => Promise<any>;
  editableStatuses?: string[];
  showCommentForSelect?: boolean;
}

const CommentsSection = <
  T extends {
    id: number;
    comment: string;
    comment_for?: string;
    status: string;
    user_id: number;
    updated_at: string;
  },
>({
  commentData,
  itemId,
  status,
  storeFn,
  updateFn,
  editableStatuses,
  showCommentForSelect = true,
}: CommentsProps<T>) => {
  const [comments, setComments] = useState<any[]>([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingComment, setEditingComment] = useState<T | null>(null);
  const [form] = Form.useForm();
  const [isLoading, setLoading] = useState(false);
  const [editable, setEditable] = useState(true);

  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const currentUser = user?.name || 'Unknown User';

  useEffect(() => {
    const isEditable = editableStatuses
      ? editableStatuses.includes(status || '')
      : !(status === 'DONE' || status === 'CANCELLED' || status === 'REJECTED');

    setEditable(isEditable);
  }, [status, editableStatuses]);

  useEffect(() => {
    const formattedComments = commentData.map((comment) => ({
      ...comment,
      updated_at: new Date(comment.updated_at).toLocaleString(),
      user: comment.user_id === user.id ? user.name : 'Admin',
    }));
    setComments(formattedComments);
  }, [commentData]);

  const handleAddOrEditComment = async () => {
    try {
      setLoading(true);
      const values = await form.validateFields();
      const newComment = {
        comment_for: values.commentFor ?? 'USER',
        comment: values.comment,
      };

      let res;
      if (editingComment) {
        res = await updateFn(Number(itemId), editingComment.id, newComment);
      } else {
        res = await storeFn(Number(itemId), newComment);
      }

      if (res?.data?.fu_comments || res?.data?.export_comments) {
        const updatedList = res.data.fu_comments || res.data.export_comments;
        const formattedComments = updatedList.map((comment: T) => ({
          ...comment,
          updated_at: new Date(comment.updated_at).toLocaleString(),
          user: comment.user_id === user.id ? user.name : 'Admin',
        }));
        setComments(formattedComments);
      }

      setIsModalVisible(false);
      setEditingComment(null);
      form.resetFields();
    } catch (error) {
      console.error('Failed to add or edit comment:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (record: T) => {
    setEditingComment(record);
    setIsModalVisible(true);
    form.setFieldsValue({
      commentFor: record.comment_for,
      comment: record.comment,
    });
  };

  const columns = [
    { title: 'Date & Time', dataIndex: 'updated_at', key: 'updated_at', width: 180 },
    { title: 'Status', dataIndex: 'status', key: 'status', width: 120 },
    { title: 'User', dataIndex: 'user', key: 'user', width: 150 },
    { title: 'Comment For', dataIndex: 'comment_for', key: 'comment_for', width: 150 },
    { title: 'Comment', dataIndex: 'comment', key: 'comment', width: 300 },
    {
      title: 'Action',
      key: 'action',
      width: 100,
      render: (_: any, record: T) => (
        <EditOutlined
          disabled={!editable || record.status !== status}
          style={{
            marginRight: 8,
            color: record.status !== status ? 'gray' : '#1890ff',
          }}
          onClick={() => record.status === status && handleEdit(record)}
        />
      ),
    },
  ];

  return (
    <>
      {editable && (
        <Button type="primary" onClick={() => setIsModalVisible(true)} icon={<PlusOutlined />} size="small">
          Add Comment
        </Button>
      )}

      <Table
        columns={columns}
        dataSource={comments}
        rowKey="id"
        style={{ marginTop: 16 }}
        pagination={{ pageSize: 5 }}
        size="small"
      />

      <Modal
        title={editingComment ? 'Edit Comment' : 'Add Comment'}
        open={isModalVisible}
        onOk={handleAddOrEditComment}
        confirmLoading={isLoading}
        onCancel={() => {
          setIsModalVisible(false);
          setEditingComment(null);
          form.resetFields();
        }}
      >
        <Form form={form} layout="vertical">
          {showCommentForSelect && currentUser === 'Admin' && (
            <Form.Item
              name="commentFor"
              label="Comment For"
              rules={[{ required: true, message: 'Please select an option' }]}
            >
              <Select placeholder="Select">
                <Select.Option value="CUSTOMER">Customer</Select.Option>
                <Select.Option value="USER">System Admin</Select.Option>
              </Select>
            </Form.Item>
          )}
          <Form.Item name="comment" label="Comment" rules={[{ required: true, message: 'Please enter a comment' }]}>
            <Input.TextArea placeholder="Enter your comment" />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default CommentsSection;
